import React from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Avatar from '@mui/material/Avatar'; // Import Avatar for the profile icon
import Navbar2 from './Navbar2';

const useStyles = makeStyles((theme) => ({
  contact: {
    minHeight: '100vh',
    backgroundImage: 'url(/images/contact.jpeg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  contactOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1,
  },
  contactTitle: {
    fontSize: '4rem',
    fontWeight: 'bold',
    fontFamily: "'Kaushan Script', cursive !important",
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('md')]: {
      fontSize: '3rem',
      paddingTop: theme.spacing(6),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.5rem',
      paddingTop: theme.spacing(8),
    },
    [theme.breakpoints.up('lg')]: {
      paddingTop: theme.spacing(4),
    },
  },
  contactSubtitle: {
    fontSize: '1.2rem',
    maxWidth: '80%',
    margin: '0 auto',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.up('lg')]: {
      maxWidth: '600px',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
  formContainer: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    borderRadius: '10px',
    padding: theme.spacing(4),
    width: '90%',
    maxWidth: '600px',
    marginTop: theme.spacing(4),
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      width: '100%',
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    '& label': {
      color: '#fff',
    },
    '& .MuiInputBase-root': {
      background: 'rgba(255, 255, 255, 0.3)',
      minHeight: '56px',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiInputBase-root': {
        background: 'rgba(255, 255, 255, 0.3)',
      },
      '& .MuiInputBase-input': {
        padding: theme.spacing(1),
        minHeight: '56px',
      },
    },
  },
  submitButton: {
    marginTop: theme.spacing(3),
    backgroundColor: '#1c3a52',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#1c3a52',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  messageBubble: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
    maxWidth: '800px',
    textAlign: 'left',
    color: '#333',
  },
  messageTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    textAlign: 'center', // Center the title
    fontFamily: "'Kaushan Script', cursive !important", // Apply the same font as "Contact Us"
  },
  founderProfile: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  founderAvatar: {
    marginRight: theme.spacing(2),
  },
}));

function Contact() {
  const classes = useStyles();

  return (
    <div>
      <Navbar2 />
      <Box id="contact" className={classes.contact}>
        <div className={classes.contactOverlay} />
        <Typography variant="h1" className={classes.contactTitle}>
          Contact Us
        </Typography>
        <Typography variant="h6" className={classes.contactSubtitle}>
          If you have any questions or need further information, feel free to contact us. We're here to help and support you in any way we can.
        </Typography>
        <Box className={classes.formContainer}>
          <form noValidate autoComplete="off">
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} mb={2}>
              <Box flex={1} mb={{ xs: 2, sm: 0 }} mr={{ sm: 1 }}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                />
              </Box>
              <Box flex={1} ml={{ sm: 1 }}>
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                />
              </Box>
            </Box>
            <Box mb={2}>
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                className={classes.formField}
              />
            </Box>
            <Box mb={2}>
              <TextField
                label="Message"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                className={classes.formField}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
            >
              Send
            </Button>
          </form>
        </Box>
        <Box className={classes.messageBubble}>
          <Typography variant="h6" className={classes.messageTitle}>
            A message from our founder
          </Typography>
          <Box className={classes.founderProfile}>
            <Avatar 
              alt="Don Govender" 
              src="/images/founder.jpg" // Add the path to your founder's image
              className={classes.founderAvatar}
              sx={{ width: 56, height: 56 }} // Adjust the size as needed
            />
            <Typography variant="body1">
              Don Govender
            </Typography>
          </Box>
          <Typography>
            In 2022, I lost my younger brother, Krish "Kitty" Govender (52). He was my friend and my pillar of support. We saw each other almost every day. His sudden passing on 1 September 2022, without any prior illnesses, hit me hard. However, what was worse was seeing the pain in the eyes of my 85-year-old mother, Saras, who doted on her younger son. A year later, she also passed on - I am convinced she died of a broken heart. In a world where we are accustomed to rewinding, undoing and repeating things, death is so unusually final and so silent - no word, no glance, no smile and no hello anymore.
            <br /><br />
            I created 4EVR.ORG.ZA in memory of my brother and mom so that I will never forget who they were and the legacy they left. I hope that 4EVR also gives you the opportunity to immortalise a loved one and in so doing also receive some small consolation for your own loss. 4EVR is an initiative of my company Lindon Corporation Pty. Ltd., a private company incorporated in South Africa in 2003.
          </Typography>
        </Box>
      </Box>
    </div>
  );
}

export default Contact;
